import React from 'react'
import {Space, Spin, Tag, Tooltip} from 'antd'
import {useLocation, useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {instance as plantsService} from '../../services/PlantsService'
import {useAsyncValue} from '../../hooks/useAsyncValue'
import {useInfiniteScroll} from '../../hooks/useInfiniteScroll'
import {PlantModal} from '../plantForm/PlantModal'
import {PlantsTableProps} from '../plantsTable/PlantsTable'
import {PlantCollection} from '../../entities/plant'
import './plantsCardListView.less'
import dayjs from 'dayjs'

const FALLBACK_COLORS = [
  'rgb(160, 122, 108)',
  'rgb(245, 129, 83)',
  'rgb(53, 171, 151)',
  'rgb(154, 83, 245)',
]

function collectionColor(collection: PlantCollection): string {
  return collection.color || FALLBACK_COLORS[collection.id % FALLBACK_COLORS.length]
}

export function PlantsCardListView({
  plants,
  getPlantLink,
  parentRoute,
  loadMore,
  hasMore,
  isLoadingMore,
}: PlantsTableProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({loadMore, hasMore, items: plants})

  const {value: plantTitlePhotos} = useAsyncValue(
    [],
    async () =>
      Promise.all(
        plants.map(async (plant) => ({
          plantId: plant.id,
          url: await plantsService.getTitlePlantPhoto(plant.titlePhotoName, plant.photos?.[0]),
        }))
      ),
    [plants]
  )

  return (
    <div className="plants-card-list">
      {plants.map((plant, index) => {
        const f = plant.fields || {}
        const photo = plantTitlePhotos[index]?.url
        const hasPhoto = !!(plant.titlePhotoName || plant.photos?.[0])
        const primaryName = f['Общераспространенное название'] || plant.name

        return (
          <div
            key={plant.id}
            className="plants-card-list__item"
            onClick={() => navigate(getPlantLink(plant) + location.search)}
          >
            <div className="plants-card-list__photo">
              {hasPhoto ? (
                <img src={photo} alt={plant.name} />
              ) : (
                <div className="plants-card-list__photo-placeholder">
                  <svg
                    width="240"
                    height="160"
                    viewBox="0 0 240 160"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    role="img"
                    aria-labelledby="title desc"
                  >
                    <g
                      opacity="0.95"
                      stroke="#6F8F72"
                      strokeWidth="2.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M96 118h48" opacity="0.35" />
                      <path d="M101 91h38l-5.5 29h-27L101 91Z" fill="#E7EFE3" />
                      {/* <path d="M108 91v-7c0-16.5 8.5-28.5 22-38" /> */}
                      <path d="M120 91V48" />
                      <path
                        d="M120 69c-10.5-8-19-9.5-28-6 3.5 9.5 13.5 14.5 28 6Z"
                        fill="#DDEBDC"
                      />
                      <path d="M121 58c9.5-10 18-13 28-10-1.5 11-12.5 18.5-28 10Z" fill="#DDEBDC" />
                      {/* <path d="M120 80c12-5.5 22-4.5 30 2-6.5 8.5-18.5 9.5-30-2Z" fill="#DDEBDC" /> */}
                      <path d="M119 47c-7-8-8.5-17-4.5-26 9 4 12.5 14.5 4.5 26Z" fill="#DDEBDC" />
                    </g>

                    <defs>
                      <linearGradient
                        id="bg"
                        x1="22"
                        y1="12"
                        x2="221"
                        y2="147"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#F6F8F1" />
                        <stop offset="0.55" stopColor="#EEF5EA" />
                        <stop offset="1" stopColor="#FAF6ED" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              )}
            </div>

            <div className="plants-card-list__info">
              <div className="plants-card-list__title">
                <div className="plants-card-list__title__name">
                  {plant.name}
                  {plant.gardenPlanVariantId && (
                    <Tooltip
                      placement="bottom"
                      title={intl.formatMessage({id: 'plantFromGardenPlan'})}
                    >
                      <Tag className="plants-card-list__garden-plan-tag" color="gold">
                        {intl.formatMessage({id: 'gardenPlanTag'})}
                      </Tag>
                    </Tooltip>
                  )}
                </div>
                {plant.dateOfPlantingUtc && (
                  <Tooltip color="white" title={intl.formatMessage({id: 'dateOfPlanting'})}>
                    <Tag className="plants-card-list__title__date">
                      🌱 {dayjs(plant.dateOfPlantingUtc).format('ll')}
                    </Tag>
                  </Tooltip>
                )}
              </div>
              <div className="plants-card-list__title__name">
                {f['Общераспространенное название'] && (
                  <div>{f['Общераспространенное название']}</div>
                )}
                {f['Сорт'] && <span className="plants-card-list__variety"> {f['Сорт']}</span>}
                {f['Уточняющая характеристика'] && (
                  <span className="plants-card-list__spec">
                    {' '}
                    ({f['Уточняющая характеристика']})
                  </span>
                )}
              </div>

              {f['Научное (латинское) название'] && (
                <div className="plants-card-list__latin">{f['Научное (латинское) название']}</div>
              )}

              {f['Другие названия'] && (
                <div className="plants-card-list__other-names">
                  {intl.formatMessage({id: 'otherNames'})}: {f['Другие названия']}
                </div>
              )}

              {plant.collections && plant.collections.length > 0 && (
                <Space className="plants-card-list__collections" size={[4, 4]} wrap>
                  {plant.collections.map((collection) => (
                    <Tag
                      key={collection.id}
                      color={collectionColor(collection)}
                      title={collection.name}
                      style={{
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {collection.name}
                    </Tag>
                  ))}
                </Space>
              )}

              {plant.projectId && (
                <div className="plants-card-list__other-names">
                  {intl.formatMessage({id: 'project'})}: {plant.projectName}
                </div>
              )}
            </div>
          </div>
        )
      })}

      <div ref={sentinelRef} />
      {isLoadingMore && (
        <div
          style={{display: 'flex', justifyContent: 'center', padding: '20px', gridColumn: '1 / -1'}}
        >
          <Spin />
        </div>
      )}
      <PlantModal routeWhenClosed={parentRoute + location.search} />
    </div>
  )
}
